import { useState } from "react";
import { PageHeader } from "@/components/PageHeader";
import { Section, Eyebrow } from "@/components/kit";
import { Reveal } from "@/components/motion";
import { PriceCards } from "@/components/PriceCards";
import { ContactForm } from "@/components/ContactForm";
import { ActLayers } from "@/components/ActLayers";
import { RoiGrid } from "@/components/RoiGrid";
import { DuoBanner } from "@/components/DuoBanner";
import { OnboardingCTA } from "@/components/OnboardingCTA";
import { MainSponsor } from "@/components/MainSponsor";
import { CommercialInventory } from "@/components/CommercialInventory";
import { SPONSOR_TIERS, SPONSOR_ACTS, SPONSOR_ROI, IMG } from "@/data/content";

export default function Patrocinadores() {
  const [prefill, setPrefill] = useState(null);
  const selectAct = (a) => setPrefill({ mensaje: `Me interesa el Acto ${a.n} · ${a.title}` });
  return (
    <>
      <PageHeader
        eyebrow="B2B · Para Patrocinadores · Co-branding · Naming"
        eyebrowColor="#FF8A80"
        title={["Sé el Naming", "Posee la identidad. del show que México está esperando."]}        
        subtitle="Elige entre ownership total con Naming Sponsor o presencia de alta frecuencia con Sponsor Co-Branding. Ambos niveles integran tu marca en los seis actos, dentro de la historia que Anto y Bella cuentan. "
      />

      <section className="px-5 sm:px-8 lg:px-12 pb-4 pt-6">
        <div className="mx-auto max-w-7xl">
          <Reveal className="relative overflow-hidden rounded-[2.5rem] shadow-[0_20px_40px_rgb(0,0,0,0.08)]">
            <img
              src={IMG.duoSponsorMerch}
              alt="Anto y Bella felices con una marca patrocinadora"
              className="w-full object-cover"
              data-testid="sponsor-merch-image"
            />
            <span className="absolute left-5 top-5 rounded-full bg-coral px-4 py-1.5 font-mono text-[11px] font-bold uppercase tracking-widest text-white">
              Tu marca, con ellas
            </span>
          </Reveal>
          <p className="mt-4 max-w-2xl text-ink/60 leading-relaxed">
            El único programa de docu-viaje neurocinematográfico de América Latina busca a su socio comercial estratégico: una marca líder que desee co-protagonizar 12 destinos icónicos junto a millones de espectadores en Occidente y más allá.
          </p>
        </div>
      </section>

      <Section className="!pt-8">
        <Reveal className="mb-10">
          <Eyebrow color="#FF8A80">Cómo participa tu patrocinio</Eyebrow>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-ink">
            Tu marca dentro de los seis actos
          </h2>
          <p className="mt-4 max-w-2xl text-ink/60 leading-relaxed">
            Como patrocinador titular tu marca vive en todo el episodio: del naming de apertura al
            legado del cierre. Cada capa muestra exactamente cómo participas. Toca la que más te
            interese y te preparamos una propuesta.
          </p>
        </Reveal>
        <ActLayers acts={SPONSOR_ACTS} onSelect={selectAct} />
          <h2 className="mt-4 font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-ink">
            Tu marca no compra un espacio. Aborda un vuelo.
          </h2>
          <p className="mt-4 max-w-2xl text-ink/60 leading-relaxed">
          Al unirte a Anto & Bella por el Mundo™ como Patrocinador, no estás comprando un anuncio de 30 segundos. Estás abordando un vuelo directo hacia la mente y el corazón de millones de "Believers" en todo el Occidente de México y más allá. Tu marca no interrumpirá la historia; se convertirá en parte de ella.
          </p>
      </Section>

      <Section alt className="!py-12">
        <CommercialInventory audience="sponsor" />
      </Section>

      <Section>
        <Reveal className="mb-10">
          <Eyebrow color="#FF8A80">ROI por episodio</Eyebrow>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-ink">
            Lo que tu patrocinio puede lograr
          </h2>
          <p className="mt-4 max-w-2xl text-ink/60 leading-relaxed">
            Seis ejemplos del retorno alcanzable como patrocinador titular en un solo episodio:
            alcance combinado, recordación y exclusividad de categoría. Cifras estimadas para
            dimensionar el impacto.
          </p>
        </Reveal>
        <RoiGrid items={SPONSOR_ROI} accent="#FF8A80" />
      </Section>

      <Section alt>
        <Reveal className="mb-12 text-center">
          <Eyebrow color="#FF8A80" className="justify-center">
            Paquetes de patrocinio en Grupo Hiperconvercloud.
          </Eyebrow>
          <h2 className="mx-auto mt-4 max-w-2xl font-display text-4xl sm:text-5xl font-extrabold leading-none tracking-tighter text-ink">
            Dos niveles de patrocinio por episodio.
          </h2>
          <p className="mx-auto mt-4 max-w-lg text-ink/60">
          Naming Sponsor para ownership total del episodio o Co-Branding para alta frecuencia y visibilidad sin naming. Ambos paquetes cubren los seis actos.
          </p>
        </Reveal>
        <PriceCards tiers={SPONSOR_TIERS} accent="#FF8A80" />
        <div className="mt-14">
          <p className="text-center font-mono text-[11px] uppercase tracking-[0.3em] text-ink/40">
            Estos espacios están disponibles ahora mismo
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-8">
            <MainSponsor variant="naming" theme="light" />
            <MainSponsor variant="slot" theme="light" label="SPONSOR MAIN" />
            <MainSponsor variant="slot" theme="light" label="SPONSOR MAIN 2" />
            <MainSponsor variant="slot" theme="light" label="SPONSOR MAIN 3" />
            <MainSponsor variant="cobranding" theme="light" label="CO-BRANDING" />
            <MainSponsor variant="cobranding" theme="light" label="CO-BRANDING 2" />
            <MainSponsor variant="cobranding" theme="light" label="CO-BRANDING 3" />
          </div>
        </div>
      </Section>

      <DuoBanner
        image={IMG.duoBiz}
        imgSide="right"
        tint="#FF8A80"
        eyebrowColor="#FF8A80"
        title="Tu marca, en buenas manos."
        text="Anto y Bella integran tu producto con naturalidad y cariño. Nada forzado: la audiencia lo vive como parte de la historia. Asocia tu marca al viaje. Presencia integrada en cada acto del episodio, no un spot intercalado: tu marca forma parte de la historia."
      />

      <OnboardingCTA
        to="/patrocinadores/onboarding"
        accent="#FF8A80"
        title="Responde 10 preguntas y recibe tu propuesta"
        text="Un cuestionario corto para conocer tu marca y enviarte un plan de patrocinio a la medida en 48 horas."
      />

      <Section id="contacto">
        <div className="grid gap-12 lg:grid-cols-[1fr_1fr]">
          <Reveal>
            <Eyebrow color="#81D4FA">Hablemos</Eyebrow>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl font-extrabold leading-none tracking-tighter text-ink">
              Diseñemos tu patrocinio.
            </h2>
            <p className="mt-6 text-lg text-ink/70 leading-relaxed">
              El itinerario de 12 destinos está listo, la plataforma web está sincronizada y las maletas están preparadas. Solo nos hace falta una cosa: el socio comercial perfecto para compartir este viaje y hacer historia juntos en la televisión abierta y digital. Cuéntanos sobre tu marca y te enviamos una propuesta a la medida,
              con calendario, entregables y proyección de audiencia.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="rounded-[2rem] bg-white p-8 shadow-[0_20px_40px_rgb(0,0,0,0.08)]">
              <ContactForm
                testidPrefix="sponsor"
                sourcePath="/patrocinadores"
                accent="#FF8A80"
                prefill={prefill}
                submitLabel="Solicitar propuesta"
                successMsg="¡Gracias! Te contactamos en 48h."
                fields={[
                  { name: "empresa", label: "Empresa", placeholder: "Nombre de tu marca" },
                  { name: "nombre", label: "Contacto", placeholder: "Tu nombre" },
                  { name: "email", label: "Email corporativo", type: "email", placeholder: "nombre@empresa.com" },
                  { name: "mensaje", label: "¿Qué buscas?", type: "textarea", placeholder: "Cuéntanos sobre tu objetivo...", required: false },
                ]}
              />
            </div>
          </Reveal>
        </div>
      </Section>
    </>
  );
}
